import {
	FiCalendar,
	FiCheckCircle,
	FiClock,
	FiEdit3,
	FiFileText,
	FiShare2,
	FiUser,
	FiUsers,
} from "react-icons/fi";

export const SUPERVISOR_PERMISSION_GROUPS = [
	{
		key: "planning",
		labelKey: "groups.supervisors.categories.planning",
		icon: FiCalendar,
		permissions: [
			{
				id: "read_only",
				inverted: true,
				labelKey: "groups.supervisors.form.canEditPlanning",
				icon: FiCalendar,
			},
			{
				id: "edit_shifts",
				labelKey: "groups.supervisors.form.canEditShifts",
				icon: FiClock,
			},
			{
				id: "edit_staffing_levels",
				labelKey: "groups.supervisors.form.canEditStaffing",
				icon: FiUsers,
			},
		],
	},
	{
		key: "team",
		labelKey: "groups.supervisors.categories.team",
		icon: FiUsers,
		permissions: [
			{
				id: "edit_employees",
				labelKey: "groups.supervisors.form.canEditEmployees",
				icon: FiUser,
			},
			{
				id: "edit_groups",
				labelKey: "groups.supervisors.form.canEditGroups",
				icon: FiShare2,
			},
			{
				id: "edit_documents",
				labelKey: "groups.supervisors.form.canEditDocuments",
				icon: FiFileText,
			},
			{
				id: "request_signaturit",
				labelKey: "groups.supervisors.form.canRequestSignaturit",
				icon: FiEdit3,
				requires: "edit_documents",
			},
		],
	},
	{
		key: "time",
		labelKey: "groups.supervisors.categories.time",
		icon: FiClock,
		permissions: [
			{
				id: "view_time_registrations",
				labelKey: "groups.supervisors.form.canViewTimeReg",
				icon: FiClock,
			},
		],
	},
	{
		key: "workflow",
		labelKey: "groups.supervisors.categories.workflow",
		icon: FiCheckCircle,
		permissions: [
			{
				id: "manage_workflows",
				labelKey: "groups.supervisors.form.canManageWorkflows",
				icon: FiCheckCircle,
			},
			{
				id: "second_level",
				labelKey: "groups.supervisors.form.secondLevel",
				icon: FiCheckCircle,
				requiresTwoLevels: true,
			},
		],
	},
];

export function getSupervisorPermissionGroups(twoLevelsWorkflow) {
	return SUPERVISOR_PERMISSION_GROUPS.map((group) => ({
		...group,
		permissions: group.permissions.filter(
			(permission) => !permission.requiresTwoLevels || twoLevelsWorkflow
		),
	})).filter((group) => group.permissions.length > 0);
}

export function isPermissionEnabled(supervisor, permission) {
	if (!supervisor) {
		return false;
	}

	if (permission.requires && !supervisor[permission.requires]) {
		return false;
	}

	if (permission.id === "view_time_registrations") {
		const value = supervisor.view_time_registrations ?? true;
		return permission.inverted ? !value : Boolean(value);
	}

	const value = supervisor[permission.id];
	return permission.inverted ? !value : Boolean(value);
}

export function isPermissionDisabled(supervisor, permission) {
	if (!permission.requires) {
		return false;
	}

	return !supervisor?.[permission.requires];
}

export function isSupervisorEmployeeMissing(employeeId) {
	return (
		employeeId === null ||
		employeeId === undefined ||
		employeeId === "" ||
		employeeId === -1 ||
		employeeId === "-1"
	);
}

export function normalizeSupervisorPermissions(supervisor) {
	if (!supervisor || supervisor.edit_documents || !supervisor.request_signaturit) {
		return supervisor;
	}

	return {
		...supervisor,
		request_signaturit: false,
	};
}

export function applySupervisorPermissionChange(supervisor, { id, checked }) {
	const next = {
		...supervisor,
		[id]: id === "read_only" ? !checked : checked,
	};

	return normalizeSupervisorPermissions(next);
}

export function getActiveSupervisorPermissionGroups(
	supervisor,
	twoLevelsWorkflow
) {
	return getSupervisorPermissionGroups(twoLevelsWorkflow)
		.map((group) => ({
			...group,
			permissions: group.permissions.filter((permission) =>
				isPermissionEnabled(supervisor, permission)
			),
		}))
		.filter((group) => group.permissions.length > 0);
}

export function getSupervisorActivePermissionCount(
	supervisor,
	twoLevelsWorkflow
) {
	return getActiveSupervisorPermissionGroups(
		supervisor,
		twoLevelsWorkflow
	).reduce((count, group) => count + group.permissions.length, 0);
}

/**
 * Empaqueta supervisores en páginas según cuántos chips de permiso ocupan.
 * Una fila "llena" (~7 permisos, 3 categorías) cabe sola; las compactas se agrupan.
 */
export const SUPERVISOR_PAGE_PERMISSION_BUDGET = 7;

export function packSupervisorsByPermissionBudget(
	supervisors,
	twoLevelsWorkflow,
	budget = SUPERVISOR_PAGE_PERMISSION_BUDGET
) {
	const rows = supervisors || [];
	if (rows.length === 0) {
		return [];
	}

	const pages = [];
	let current = [];
	let used = 0;

	rows.forEach((supervisor) => {
		const cost = Math.max(
			1,
			getSupervisorActivePermissionCount(supervisor, twoLevelsWorkflow)
		);
		if (current.length > 0 && used + cost > budget) {
			pages.push(current);
			current = [];
			used = 0;
		}
		current.push(supervisor);
		used += cost;
	});

	if (current.length > 0) {
		pages.push(current);
	}

	return pages;
}
